import { useCallback, useEffect, useRef, useState } from 'react';
import type { MailClient } from '../api/client';
import { MailError, packId, unpackId, type Email, type EmailBodyPart, type Identity } from '../api/types';
import type { OutgoingMessage } from '../api/client';
import { attachmentsToCarry, buildDraft, buildHtmlDraft, parseAddresses, type ComposeMode } from '../mail/quote';
import { extractInlineImages, hasContent, htmlToText, sanitiseOutgoing, textToHtml } from '../mail/html';
import { cachedIdentities, loadIdentities } from '../mail/identities';
import { beforeUnloadGuard, closeAction } from '../mail/unsaved';
import { recents, remember as rememberRecipients, type Suggestion } from '../mail/recents';
import { fromContacts, merge } from '../mail/directory';
import RecipientInput from './RecipientInput';
import { fileSize } from '../mail/format';
import { useT } from '../i18n';
import { useDrag } from './Resizer';
import { useTheme } from '../theme/store';
import { useOverlay } from '../ui/overlays';
import { usePanes, useWindowHeight, useWindowWidth } from '../ui/viewport';
import RichText from './RichText';

/*
    Docked composer.

    It writes both alternatives, always. A rich message ships an HTML part and a
    plain part generated from it, so a recipient with images off, a screen reader
    or a watch gets something readable rather than "(no readable body)" — and a
    plain message ships only text, because dressing it up in HTML would be a lie
    about what was written.
*/

interface Attachment {
    name: string;
    type: string;
    size: number;
    /** The bytes, once they are in hand. Absent while one is still being fetched back. */
    file?: File;
    /** Set on parts that are referenced from the HTML body rather than listed. */
    cid?: string | null;
    /** Where it came from, on a forward or a reopened draft, until its bytes arrive. */
    partId?: string;
    loading?: boolean;
    error?: string;
}

export interface ComposeTarget {
    mode: ComposeMode;
    source: Email | null;
    /**
     * Somebody to write to, when the composer was opened from a person rather than from a message
     * — the address book's "write to" button. Only meaningful for a new message; a reply takes its
     * recipients from what it is replying to.
     */
    to?: string;
}

const RICH_KEY = 'webmail.compose.rich.v1';

/*
    Attachments carried over from the message being forwarded, or from a draft being reopened.

    IMAP has no handle a new message could simply point at — no blob id that says "the same bytes
    as over there" — so anything carried forward is fetched back down and attached again. It starts
    as a placeholder with the right name and size so the list is right immediately, and the bytes
    arrive behind it.
*/
const carried = (parts: EmailBodyPart[]): Attachment[] =>
    parts.map((p) => ({
        name: p.name ?? 'attachment',
        type: p.type,
        size: p.size,
        cid: p.cid,
        partId: p.partId,
        loading: true,
    }));

const askPrimary: React.CSSProperties = {
    height: 32,
    padding: '0 16px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--r-input)',
    cursor: 'pointer',
    fontSize: 'var(--fs-md)',
};

const askPlain: React.CSSProperties = {
    height: 32,
    padding: '0 14px',
    background: 'transparent',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-input)',
    cursor: 'pointer',
    fontSize: 'var(--fs-md)',
};

export default function Compose({
    client,
    accountId,
    target,
    onClose,
    onSent,
}: {
    client: MailClient | undefined;
    accountId: string | undefined;
    target: ComposeTarget;
    draftsMailboxId: string | null;
    sentMailboxId: string | null;
    onClose: () => void;
    onSent: () => void;
}) {
    const { t, lang } = useT();
    // The composer owns the keyboard while it is open. It used to be recognised
    // by a ref the mailbox held; now that it is mounted by the shell instead,
    // it says so itself.
    useOverlay();
    const [identities, setIdentities] = useState<Identity[]>(() =>
        accountId && client ? (cachedIdentities(client, accountId) ?? []) : [],
    );
    // Nothing is editable until the draft is seeded. Accepting keystrokes into a
    // composer that is about to overwrite itself is how typing gets lost.
    const [ready, setReady] = useState(false);
    const [identityId, setIdentityId] = useState<string | null>(null);
    const [to, setTo] = useState('');
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [showCc, setShowCc] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [html, setHtml] = useState('');
    const [rich, setRich] = useState(() => localStorage.getItem(RICH_KEY) !== '0');
    const [seed, setSeed] = useState('0');
    const [directory, setDirectory] = useState<Suggestion[]>([]);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [status, setStatus] = useState<'idle' | 'holding' | 'sending' | 'error'>('idle');
    // Seconds still on the clock while a sent message can be taken back.
    const [left, setLeft] = useState(0);
    // Depth counter, not a boolean: dragging over a child fires dragleave on the
    // parent, and a boolean flickers the overlay off halfway across the window.
    const [dragging, setDragging] = useState(0);
    const holdRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const sendRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [full, setFull] = useState(false);
    /*
        On a phone the floating window is not a window: it would cover the screen
        anyway, minus a rounded border and a grip for a drag nobody can aim. So
        there it is always full, and the button that would toggle it is gone
        rather than present and pointless.
    */
    const { drawerNav: phone } = usePanes();
    const expanded = full || phone;
    // Read live rather than at render: a clamp measured once is wrong the moment
    // the window changes, and on a phone it changes every time it is turned.
    const windowW = useWindowWidth();
    const windowH = useWindowHeight();
    const undoSend = useTheme((s) => s.undoSend);
    const composeW = useTheme((s) => s.composeW);
    const composeH = useTheme((s) => s.composeH);
    const setPaneSize = useTheme((s) => s.setPaneSize);
    const size = { w: composeW, h: composeH };
    // Dragging the top-left grip: moving left/up makes it bigger.
    const grip = useDrag((dx, dy) =>
        setPaneSize({
            composeW: Math.min(Math.max(composeW - dx, 380), window.innerWidth - 40),
            composeH: Math.min(Math.max(composeH - dy, 280), window.innerHeight - 40),
        }),
    );
    const seedRef = useRef<{ inReplyTo: string[] | null; references: string[] | null }>({ inReplyTo: null, references: null });
    const fileRef = useRef<HTMLInputElement>(null);
    /*
        The draft currently representing this composer on the server, if any.

        ONE ref, deliberately. This used to be two — a packed "folder:uid" string and the uid on
        its own — and only the uid was updated after a save. Nothing went wrong while the
        composer saved once and closed immediately, but it made autosaving impossible to add
        safely: send and discard both deleted the *stale* identity, so every autosaved copy
        stayed in Drafts for ever.

        IMAP cannot change a message in place, so each save appends a new one and the server
        erases the one it replaces. That means this value changes on every save, and everything
        that acts on "the draft" has to read it from here.
    */
    const draftRef = useRef<{ folder: string; uid: number } | null>(
        target.mode === 'draft' && target.source?.uid
            ? { folder: unpackId(target.source.id).folder, uid: target.source.uid }
            : null,
    );
    const pristine = useRef('');
    // One save at a time. Two in flight would both name the same message as the one they
    // replace: both append, and only one of the two old copies is ever removed.
    const savingRef = useRef<Promise<void> | null>(null);
    const lastSaveAt = useRef(Date.now());
    // Set when the server says the session is gone. The composer stays open and stops saving;
    // silently swallowing this is how twenty minutes of writing disappears.
    const [sessionLost, setSessionLost] = useState(false);
    // The close question is on screen.
    const [asking, setAsking] = useState(false);

    /*
        One shape for "what is in this composer", used by every dirty check.

        It used to be written two ways — one including bcc and one hard-coding it empty — so the
        answer to "has this changed?" depended on which line asked.
    */
    const snapshot = () =>
        JSON.stringify([to, cc, bcc, subject, body, html]);

    const identity = identities.find((i) => i.id === identityId) ?? identities[0] ?? null;

    useEffect(() => {
        if (!client || !accountId) return;
        let cancelled = false;
        void (async () => {
            try {
                const list = await loadIdentities(client, accountId);
                const first = list[0] ?? null;
                if (cancelled) return;
                setIdentities(list);
                setIdentityId(first?.id ?? null);

                const draft = buildDraft(target.mode, target.source, first?.email ?? '', first?.textSignature ?? '');
                const richBody = buildHtmlDraft(target.mode, target.source, first?.htmlSignature ?? '');
                // A recipient the composer was opened WITH counts as part of the untouched draft,
                // not as something typed: opening a message to somebody and closing it again is
                // not an unsaved message.
                const seededTo = target.to ?? draft.to;
                setTo(seededTo);
                setCc(draft.cc);
                setBcc('');
                setShowCc(Boolean(draft.cc));
                setSubject(draft.subject);
                setBody(draft.body);
                setHtml(richBody);
                setSeed(`${target.mode}-${first?.id ?? 'none'}`);
                // A draft that was written in HTML reopens in HTML whatever the
                // preference says — opening it plain would silently flatten it.
                if (target.mode === 'draft' && target.source?.html?.trim()) setRich(true);
                setAttachments(carried(attachmentsToCarry(target.mode, target.source)));
                seedRef.current = { inReplyTo: draft.inReplyTo, references: draft.references };
                pristine.current = JSON.stringify([seededTo, draft.cc, '', draft.subject, draft.body, richBody]);
                setReady(true);
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : String(e));
                    // A failed identity fetch must not leave a composer nobody can
                    // type in — the message still sends, it just has no signature.
                    setReady(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [client, accountId, target]);

    /*
        Suggestions come from two places now.

        This browser's own history is instant and is set first, so the field is useful before
        anything has been fetched. The address book follows when it arrives and is merged in — it
        holds people who were written down but never mailed, which is exactly who the history
        cannot know about.

        An installation with no address book server answers null, and then nothing changes: the
        suggestions are what they always were rather than an error nobody can act on.
    */
    useEffect(() => {
        const local = recents();
        setDirectory(local);
        if (!client) return;
        let cancelled = false;
        void client
            .contacts()
            .then((book) => {
                if (cancelled || !book) return;
                setDirectory(merge(local, fromContacts(book.contacts)));
            })
            .catch(() => {
                /* the composer works without the book; it is a better guess, not a requirement */
            });
        return () => {
            cancelled = true;
        };
    }, [target, client]);

    /*
        Fetches the bytes of anything carried over from a forward or a reopened draft.

        It runs after the composer is already on screen and typeable: waiting for a 12 MB
        attachment before showing the window would be the wrong way round, and the send button
        stays disabled until they have all landed.
    */
    useEffect(() => {
        if (!client) return;
        const source = target.source;
        if (!source) return;
        let cancelled = false;

        void (async () => {
            for (const part of attachmentsToCarry(target.mode, source)) {
                try {
                    const blob = await client.downloadAttachment(source.id, part.partId);
                    if (cancelled) return;
                    const file = new File([blob], part.name ?? 'attachment', {
                        type: part.type || 'application/octet-stream',
                    });
                    setAttachments((list) =>
                        list.map((a) => (a.partId === part.partId ? { ...a, file, loading: false } : a)),
                    );
                } catch (e) {
                    if (cancelled) return;
                    const message = e instanceof Error ? e.message : String(e);
                    setAttachments((list) =>
                        list.map((a) => (a.partId === part.partId ? { ...a, loading: false, error: message } : a)),
                    );
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [client, target]);

    /*
        Switching identity re-seeds the body only while nothing has been typed.
        Gmail swaps the signature whatever you've written and takes your text
        with it if you were unlucky; leaving a stale signature is the smaller
        wrong, and it is visible.
    */
    const chooseIdentity = (id: string) => {
        setIdentityId(id);
        const next = identities.find((i) => i.id === id);
        const untouched = snapshot() === pristine.current;
        if (!next || !untouched) return;
        const draft = buildDraft(target.mode, target.source, next.email, next.textSignature ?? '');
        const richBody = buildHtmlDraft(target.mode, target.source, next.htmlSignature ?? '');
        setBody(draft.body);
        setHtml(richBody);
        setSeed(`${target.mode}-${next.id}`);
        pristine.current = JSON.stringify([target.to ?? draft.to, draft.cc, '', draft.subject, draft.body, richBody]);
    };

    /*
        Switching format converts what's there rather than starting over. Rich to
        plain is lossy by definition, so it goes through the same htmlToText that
        builds the plain alternative of every sent message — what you see after
        switching is exactly what a plain-text recipient would have received.
    */
    const setFormat = (next: boolean) => {
        if (next === rich) return;
        localStorage.setItem(RICH_KEY, next ? '1' : '0');
        if (next) {
            setHtml(textToHtml(body));
            setSeed(`plain-to-rich-${Date.now()}`);
        } else {
            setBody(htmlToText(html));
        }
        setRich(next);
    };

    /*
        Nothing is uploaded when a file is picked.

        Iris Mail uploaded each one straight away and sent a reference. Here the file goes with
        the message, so it is simply held until send — which also means a file attached to a
        message that is never sent leaves nothing behind anywhere.
    */
    const addFiles = (files: Iterable<File> | FileList | null) => {
        if (!files) return;
        const picked = Array.from(files).map((file) => ({
            name: file.name,
            type: file.type || 'application/octet-stream',
            size: file.size,
            file,
        }));
        if (picked.length) setAttachments((a) => [...a, ...picked]);
    };

    const recipients = parseAddresses(to);
    const ccList = parseAddresses(cc);
    const bccList = parseAddresses(bcc);
    const bad = [...recipients.invalid, ...ccList.invalid, ...bccList.invalid];
    const uploading = attachments.some((a) => a.loading);
    const canSend =
        ready &&
        Boolean(client && identity) &&
        recipients.valid.length > 0 &&
        bad.length === 0 &&
        !uploading &&
        status !== 'sending';

    /*
        Builds the message. Inline images are uploaded here rather than when they
        are pasted, because a picture dropped into a body that never gets sent
        should not leave a blob behind on the server.
    */
    /*
        Builds the message the backend takes: the fields, and the files themselves.

        Inline images are turned into attachments here rather than when they are pasted, because
        a picture dropped into a body that never gets sent should not have gone anywhere. Each one
        keeps the cid the HTML refers to it by, so a logo in a signature travels inside the message
        and shows even in a client that blocks remote images.
    */
    const buildMessage = useCallback(async (): Promise<OutgoingMessage> => {
        const files: File[] = [];
        const cids: string[] = [];

        for (const a of attachments) {
            if (!a.file) continue;
            files.push(a.file);
            cids.push(a.cid ?? '');
        }

        let htmlOut = '';
        if (rich) {
            const clean = sanitiseOutgoing(html);
            const { html: withCids, images } = extractInlineImages(clean, files.length);
            htmlOut = withCids;
            for (const image of images) {
                files.push(new File([image.blob], image.name, { type: image.type }));
                cids.push(image.cid);
            }
        }

        return {
            fromName: identity?.name || undefined,
            to: recipients.valid,
            cc: ccList.valid,
            bcc: bccList.valid,
            subject,
            text: rich ? htmlToText(htmlOut) : body,
            ...(rich ? { html: htmlOut } : {}),
            ...(seedRef.current.inReplyTo?.length ? { inReplyTo: seedRef.current.inReplyTo[0] } : {}),
            ...(seedRef.current.references ? { references: seedRef.current.references } : {}),
            attachments: files,
            attachmentCids: cids,
        };
    }, [attachments, rich, html, body, identity, recipients.valid, ccList.valid, bccList.valid, subject]);

    /*
        Closing keeps the work. Anything typed is written to Drafts on the server
        rather than held in the tab, so a closed window or a crashed browser
        doesn't lose it — which is the whole reason drafts exist.
    */
    /*
        Write the current state to Drafts, replacing whatever this composer wrote last.

        Serialised through savingRef: two of these in flight would both name the same message as
        the one they replace, so both would append and only one old copy would ever be removed —
        a duplicate draft that nothing later cleans up.
    */
    /*
        Says whether the draft reached the server, because the caller closes the window on the
        strength of it.

        It does not refuse to try once the session has been seen dead. The warning tells the
        person to sign in again in another tab and then close this window — the cookie is shared,
        so that save works — and an early return here made that advice a lie: the save was never
        attempted again. Saves only happen when someone asks for one, so trying again cannot turn
        into a loop.
    */
    const saveNow = async (): Promise<boolean> => {
        if (!client) return false;
        const previous = savingRef.current;
        let ok = false;
        const run = (async () => {
            if (previous) await previous.catch(() => {});
            const taken = snapshot();
            try {
                const saved = await client.saveDraft(await buildMessage(), draftRef.current?.uid);
                draftRef.current = { folder: saved.folder, uid: saved.uid };
                // Refreshed here or `changed` stays true for ever and every close rewrites
                // bytes the server already has.
                pristine.current = taken;
                lastSaveAt.current = Date.now();
                setSessionLost(false);
                ok = true;
            } catch (e) {
                // A dead session must not be swallowed: the person is still typing into a
                // window whose every save is now failing.
                if (e instanceof MailError && e.status === 401) setSessionLost(true);
                else setError(t('compose.saveFailed'));
            }
        })();
        savingRef.current = run;
        try {
            await run;
        } finally {
            if (savingRef.current === run) savingRef.current = null;
        }
        return ok;
    };

    /*
        Closing asks rather than deciding.

        It used to save silently, which is the friendlier default right up to the moment somebody
        closes a window they meant to throw away and finds it in Drafts — and, with the replace
        chain being what it is, finds three of them.
    */
    const close = async () => {
        switch (closeAction({ dirty, empty: emptyNow, hasDraft: draftRef.current !== null })) {
            case 'ask':
                setAsking(true);
                return;
            case 'delete':
                if (client && draftRef.current) {
                    await client
                        .destroyEmail('', packId(draftRef.current.folder, draftRef.current.uid))
                        .catch(() => {});
                    draftRef.current = null;
                }
                break;
        }
        onClose();
    };

    /** Answered the question with "keep it". */
    const saveAndClose = async () => {
        setError(null);
        const saved = await saveNow();
        setAsking(false);
        /*
            Closed only once the draft is on the server. It used to close whatever the save had
            done — with the session gone, the warning below flashed for the instant before the
            window shut, and everything written went with it (measured 2026-10-12). What is on
            screen is now the only copy, so the window stays and says why.
        */
        if (saved) onClose();
    };

    /** Answered with "no": nothing is written, and whatever the server already had stays. */
    const closeWithoutSaving = () => {
        setAsking(false);
        onClose();
    };

    /*
        Throw it away.

        Closing keeps a draft, which is the right default and the wrong one when
        you have decided the message shouldn't exist — without this the only way
        to be rid of it is to close, go to Drafts and delete it there.
    */
    const discard = async () => {
        // Wait for any save on its way, or it lands after the delete and resurrects the draft.
        if (savingRef.current) await savingRef.current.catch(() => {});
        if (client && accountId && draftRef.current) {
            await client
                .destroyEmail(accountId, packId(draftRef.current.folder, draftRef.current.uid))
                .catch(() => {});
            draftRef.current = null;
        }
        onClose();
    };

    /*
        Unsaved work, and the browser's own warning about it.

        There is no timer here on purpose. Autosaving was built and then withdrawn: each save
        appends a new message and asks the server to erase the previous one, and that erase is
        not reliable — the server reports success while the old copy survives (ledger İ-07). A
        loop on top of that fills Drafts with copies. Saving happens when somebody says so.

        beforeunload covers the ordinary "closed the browser" case. It cannot cover a crash, and
        the guard is deliberately narrow: warning on every close is how people learn to click
        through the warning that mattered.
    */
    const dirty = snapshot() !== pristine.current;
    const emptyNow = !to.trim() && !cc.trim() && !subject.trim() && !(rich ? hasContent(html) : Boolean(body.trim()));
    const guardRef = useRef(false);
    guardRef.current = beforeUnloadGuard({ dirty, empty: emptyNow, hasDraft: draftRef.current !== null });

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!guardRef.current) return;
            e.preventDefault();
            // Browsers ignore custom text now and show their own; returnValue is still what
            // makes the prompt appear at all.
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    // Escape closes — and closing saves. Held in a ref so the listener always
    // sees the current field values rather than the ones from first render.
    const closeRef = useRef(close);
    closeRef.current = close;
    // Assigned below, once undo exists — the escape listener only reads it on a
    // keypress, long after this render has finished.
    const undoRef = useRef<() => void>(() => {});
    // Same shape as undoRef: the listener is installed once and must reach whatever send is by
    // the time a key is pressed, not the one that existed at mount.
    const sendKeyRef = useRef<() => void>(() => {});
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                // Mid-countdown, escape takes the message back rather than
                // closing over a send that is still about to happen.
                if (holdRef.current) undoRef.current();
                else void closeRef.current();
                return;
            }
            /*
                Sending from the keyboard, in the two shapes every mail client agrees on:
                ctrl/cmd+enter (Outlook, Gmail, Thunderbird, Apple Mail all) and alt+s, which is
                Outlook's own. Both are bound rather than one, because a person arrives here with
                the habit their last client gave them.

                Not remappable like the letter shortcuts are: those only fire outside a text
                field, and this one has to work with the cursor in the message. Neither
                combination means anything to the browser, which is why these two and not
                ctrl+r or ctrl+f — those are the browser's reload and find, and taking them
                from somebody is not a shortcut, it is a trap.
            */
            const send = (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) || (e.altKey && e.code === 'KeyS');
            if (send) {
                e.preventDefault();
                e.stopPropagation();
                sendKeyRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const submit = async () => {
        if (!client || !identity) return;
        setStatus('sending');
        setError(null);
        try {
            const everyone = [...recipients.valid, ...ccList.valid, ...bccList.valid];
            await client.sendEmail(await buildMessage());
            rememberRecipients(everyone);
            // The message is away; the draft it came from is now noise. draftRef is the copy
            // the last save wrote, not the one the composer opened with — deleting the latter
            // is what used to leave an autosaved duplicate behind for ever.
            if (draftRef.current) {
                await client
                    .destroyEmail('', packId(draftRef.current.folder, draftRef.current.uid))
                    .catch(() => {});
                draftRef.current = null;
            }
            onSent();
        } catch (e) {
            setStatus('error');
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    /*
        Undo send.

        The composer stays mounted through the countdown rather than closing and
        being rebuilt from a draft on undo — every field, the attachments and the
        cursor are still exactly where they were, so taking a message back leaves
        you in the composer you were in, not a reconstruction of it.

        Nothing has been handed to the server yet during the hold, so undo is
        genuinely a cancellation and not a recall.
    */
    /*
        Two timers, on purpose.

        The first version counted down inside a setLeft updater and called
        submit() when it reached zero — which sent the message TWICE. React may
        invoke a state updater more than once (it does exactly that in
        StrictMode), because updaters are required to be pure, and a send is
        about as impure as it gets. It was caught in the mailbox: one click on
        Send, two messages with different Message-IDs.

        So the countdown is display only, and the send is a single timeout that
        can only fire once.
    */
    const stopHold = () => {
        if (holdRef.current) clearInterval(holdRef.current);
        if (sendRef.current) clearTimeout(sendRef.current);
        holdRef.current = null;
        sendRef.current = null;
    };

    const send = () => {
        // canSend already excludes 'sending'; 'holding' is the double-click case.
        if (!canSend || status === 'holding') return;
        if (undoSend <= 0) {
            void submit();
            return;
        }
        setError(null);
        setStatus('holding');
        setLeft(undoSend);
        holdRef.current = setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
        sendRef.current = setTimeout(() => {
            stopHold();
            void submit();
        }, undoSend * 1000);
    };

    const undo = () => {
        stopHold();
        setStatus('idle');
    };

    undoRef.current = undo;
    sendKeyRef.current = send;

    const sendNow = () => {
        if (status !== 'holding') return;
        stopHold();
        void submit();
    };

    // A composer torn down mid-countdown must not fire a send into nothing.
    useEffect(() => () => {
        if (holdRef.current) clearInterval(holdRef.current);
        if (sendRef.current) clearTimeout(sendRef.current);
    }, []);

    /*
        There is no scheduled send.

        It needs the mail server to accept a message and hold it, which is an SMTP extension
        (FUTURERELEASE) their Postfix does not offer. The alternative — this browser keeping the
        message and sending it at the right moment — only works while the tab is open, which is
        exactly when somebody is not at their desk. A button that usually works is worse than no
        button.
    */

    const field: React.CSSProperties = {
        width: '100%',
        height: 32,
        padding: '0 10px',
        background: 'var(--surface2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-btn)',
        outline: 'none',
        fontSize: 'var(--fs-md)',
    };

    const label: React.CSSProperties = {
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-2xs)',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: 'var(--text-mute)',
        width: 44,
        flex: '0 0 auto',
    };

    return (
        <div
            style={
                expanded
                    ? {
                          position: 'fixed',
                          // Full screen means the whole screen on a phone; on a desktop a
                          // margin still says the app is underneath.
                          inset: phone ? 0 : 24,
                          zIndex: 60,
                          display: 'flex',
                          flexDirection: 'column',
                      }
                    : {
                          position: 'fixed',
                          right: 20,
                          bottom: 20,
                          width: Math.min(size.w, windowW - 40),
                          height: Math.min(size.h, windowH - 40),
                          zIndex: 60,
                          display: 'flex',
                          flexDirection: 'column',
                      }
            }
            onDragEnter={(e) => {
                if (e.dataTransfer.types.includes('Files')) setDragging((n) => n + 1);
            }}
            onDragOver={(e) => {
                if (e.dataTransfer.types.includes('Files')) e.preventDefault();
            }}
            onDragLeave={() => setDragging((n) => Math.max(0, n - 1))}
            onDrop={(e) => {
                if (!e.dataTransfer.files.length) return;
                e.preventDefault();
                setDragging(0);
                void addFiles(e.dataTransfer.files);
            }}
            /*
                Pasting a file attaches it. Screenshots are the whole point —
                someone hits PrintScreen and pastes, and the alternative is
                saving to disk first just to pick it back up in a file dialog.
                Text pastes are left alone and handled by whichever field has
                focus.
            */
            onPaste={(e) => {
                const files = Array.from(e.clipboardData.files);
                if (!files.length) return;
                e.preventDefault();
                void addFiles(files);
            }}
        >
            {dragging > 0 ? (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 62,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'color-mix(in srgb, var(--accent) 22%, transparent)',
                        border: '2px dashed var(--accent)',
                        borderRadius: 'var(--r-panel)',
                        pointerEvents: 'none',
                        fontSize: 'var(--fs-md)',
                        fontWeight: 600,
                        color: 'var(--text)',
                    }}
                >
                    {t('compose.dropHere')}
                </div>
            ) : null}
            {!expanded ? (
                // Anchored bottom-right, so it grows from the top-left corner.
                <div
                    {...grip}
                    title="Drag to resize"
                    style={{
                        position: 'absolute',
                        left: -4,
                        top: -4,
                        width: 16,
                        height: 16,
                        cursor: 'nwse-resize',
                        touchAction: 'none',
                        zIndex: 61,
                        borderTop: '2px solid var(--border2)',
                        borderLeft: '2px solid var(--border2)',
                        borderTopLeftRadius: 6,
                    }}
                />
            ) : null}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    background: 'var(--surface)',
                    border: '1px solid var(--border2)',
                    /*
                        Pencere kabuğu (arayüz v2, 7. adım). Teslimin yapı hakkında söylediği tek
                        şey "aynı kalıyor" — değişen yalnız kabuk: daha büyük yarıçap ve daha derin
                        bir gölge. Yazma penceresi ekranın üstünde DURAN tek şey; sığ bir gölge onu
                        sayfaya yapıştırıyor ve altında bir uygulama olduğunu unutturuyordu.
                    */
                    borderRadius: 'var(--r-modal)',
                    boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--border)',
                        background: 'var(--surface2)',
                    }}
                >
                    <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>
                        {t(target.mode === 'new' ? 'action.newMessage' : target.mode === 'forward' ? 'action.forward' : 'action.reply')}
                    </span>
                    {/*
                        Whether anything would be lost by closing, shown rather than left to be
                        discovered. Quiet on purpose — it is a state, not a warning.
                    */}
                    {dirty && !emptyNow ? (
                        <span className="kicker" style={{ fontSize: 'var(--fs-xs)', opacity: 0.75 }}>
                            · {t('compose.unsaved')}
                        </span>
                    ) : null}
                    {identities.length > 1 ? (
                        <select
                            value={identity?.id ?? ''}
                            onChange={(e) => chooseIdentity(e.target.value)}
                            title={t('compose.from')}
                            style={{
                                height: 24,
                                maxWidth: 240,
                                padding: '0 6px',
                                background: 'var(--surface)',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-btn)',
                                color: 'var(--text-dim)',
                                fontSize: 'var(--fs-xs)',
                            }}
                        >
                            {identities.map((i) => (
                                <option key={i.id} value={i.id}>
                                    {i.name ? `${i.name} <${i.email}>` : i.email}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span className="kicker">// {identity ? identity.email : t('compose.loadingIdentity')}</span>
                    )}
                    <div style={{ flex: 1 }} />
                    {phone ? null : (
                        <button onClick={() => setFull((v) => !v)} title={t(full ? 'compose.dock' : 'compose.fullScreen')} style={chrome}>
                            {full ? '⤡' : '⤢'}
                        </button>
                    )}
                    <button data-testid="compose-close" onClick={() => void close()} title={t('compose.closeKeepsDraft')} style={chrome}>
                        ×
                    </button>
                </div>

                <div style={{ padding: 12, display: 'grid', gap: 8, flex: 1, minHeight: 0, gridTemplateRows: 'auto auto auto auto 1fr auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={label}>{t('compose.to')}</span>
                        <RecipientInput testId="compose-to" disabled={!ready} value={to} onChange={setTo} directory={directory} placeholder="name@example.com" />
                        {!showCc ? (
                            <button onClick={() => setShowCc(true)} className="kicker" style={{ background: 'transparent', border: 'none' }}>
                                {t('compose.ccBcc')}
                            </button>
                        ) : null}
                    </div>

                    {showCc ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={label}>{t('compose.cc')}</span>
                                <RecipientInput disabled={!ready} value={cc} onChange={setCc} directory={directory} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={label}>{t('compose.bcc')}</span>
                                <RecipientInput disabled={!ready} value={bcc} onChange={setBcc} directory={directory} />
                            </div>
                        </>
                    ) : (
                        <>
                            <span />
                            <span />
                        </>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={label}>{t('compose.subject')}</span>
                        {/*
                            Erişilebilir adı YOKTU. Alanın solundaki "Konu" yazısı görsel bir
                            etiket ama `<label>` değil, yani dinleyen kişiye bu kutu isimsiz
                            geliyordu. Açık yazma penceresi bugüne kadar hiç axe ile taranmamıştı.
                        */}
                        <input
                            data-testid="compose-subject"
                            aria-label={t('compose.subject')}
                            disabled={!ready}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            style={field}
                        />
                    </div>

                    {!ready ? (
                        <div style={{ display: 'grid', placeItems: 'center', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)', background: 'var(--surface2)', minHeight: 160 }}>
                            <span className="kicker">// {t('compose.loadingIdentity')}</span>
                        </div>
                    ) : rich ? (
                        <RichText value={html} seed={seed} onChange={setHtml} placeholder={t('rich.placeholder.compose')} />
                    ) : (
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            spellCheck
                            dir="auto"
                            style={{
                                width: '100%',
                                height: '100%',
                                minHeight: 160,
                                resize: 'none',
                                padding: 11,
                                background: 'var(--surface2)',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-btn)',
                                outline: 'none',
                                fontFamily: 'var(--font-sans)',
                                fontSize: 'var(--fs-md)',
                                lineHeight: 1.6,
                            }}
                        />
                    )}

                    <div style={{ display: 'grid', gap: 8 }}>
                        {attachments.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {attachments.map((a, i) => (
                                    <span
                                        key={`${a.name}-${i}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 7,
                                            padding: '4px 8px',
                                            fontSize: 'var(--fs-xs)',
                                            borderRadius: 'var(--r-pill)',
                                            background: a.error ? 'var(--red-bg)' : 'var(--chip-bg)',
                                            border: `1px solid ${a.error ? 'var(--red)' : 'var(--border)'}`,
                                            color: a.error ? 'var(--red)' : 'var(--text)',
                                        }}
                                    >
                                        {a.name}
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-mute)' }}>
                                            {a.error ? t('compose.failed') : a.file ? fileSize(a.size, lang === 'tr' ? 'tr-TR' : 'en-GB') : t('compose.uploading')}
                                        </span>
                                        <button
                                            onClick={() => setAttachments((list) => list.filter((_, j) => j !== i))}
                                            style={{ background: 'transparent', border: 'none', color: 'inherit', padding: 0 }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : null}

                        {bad.length ? (
                            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--amber)' }}>
                                {t('compose.badAddress', { list: bad.join(', ') })}
                            </div>
                        ) : null}

                        {sessionLost ? (
                            /*
                                Not dismissable, and the window does not close.

                                A dead session used to be swallowed here: the save failed, the
                                composer closed anyway, and the message was gone with nothing
                                said. Twenty minutes of writing can sit behind this warning, so
                                it stays on screen until the person does something about it.
                            */
                            <div
                                role="alert"
                                style={{
                                    fontSize: 'var(--fs-sm)',
                                    lineHeight: 1.45,
                                    color: 'var(--amber)',
                                    border: '1px solid var(--amber)',
                                    borderRadius: 'var(--r-input)',
                                    padding: '8px 10px',
                                }}
                            >
                                {t('compose.sessionLost')}
                            </div>
                        ) : null}
                        {error ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{error}</div> : null}

                        {asking ? (
                            /*
                                The question, in place of the ordinary actions.

                                Put here rather than in a floating dialog so it cannot be missed
                                and cannot be dismissed by clicking beside it: the answer decides
                                whether what is on screen survives.
                            */
                            <div
                                role="alertdialog"
                                aria-label={t('compose.saveQuestion')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                    padding: '10px 12px',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--r-input)',
                                    background: 'var(--chip-bg)',
                                }}
                            >
                                <span style={{ fontSize: 'var(--fs-md)', flex: 1, minWidth: 200 }}>{t('compose.saveQuestion')}</span>
                                <button data-testid="compose-save-draft" onClick={() => void saveAndClose()} style={askPrimary}>
                                    {t('compose.saveDraft')}
                                </button>
                                <button onClick={closeWithoutSaving} style={askPlain}>
                                    {t('compose.dontSave')}
                                </button>
                                <button onClick={() => setAsking(false)} style={askPlain} autoFocus>
                                    {t('compose.keepWriting')}
                                </button>
                            </div>
                        ) : null}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                            {status === 'holding' ? (
                                <>
                                    <button
                                        data-testid="undo-send"
                                        onClick={undo}
                                        style={{
                                            height: 34,
                                            padding: '0 18px',
                                            background: 'var(--amber)',
                                            color: '#1a1408',
                                            border: 'none',
                                            borderRadius: 'var(--r-btn)',
                                            fontSize: 'var(--fs-md)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('compose.undo', { seconds: String(left) })}
                                    </button>
                                    <button
                                        onClick={sendNow}
                                        style={{
                                            height: 34,
                                            padding: '0 12px',
                                            background: 'transparent',
                                            border: '1px solid var(--border2)',
                                            borderRadius: 'var(--r-btn)',
                                            color: 'var(--text-dim)',
                                            fontSize: 'var(--fs-sm)',
                                        }}
                                    >
                                        {t('compose.sendNow')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={send}
                                        disabled={!canSend}
                                        style={{
                                            height: 34,
                                            padding: '0 18px',
                                            background: canSend ? 'var(--accent)' : 'var(--chip-bg)',
                                            color: canSend ? 'var(--on-accent)' : 'var(--text-mute)',
                                            border: 'none',
                                            borderRadius: 'var(--r-btn)',
                                            fontSize: 'var(--fs-md)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t(status === 'sending' ? 'compose.sending' : 'compose.send')}
                                    </button>
                                </>
                            )}

                            <input
                                ref={fileRef}
                                type="file"
                                multiple
                                onChange={(e) => void addFiles(e.target.files)}
                                style={{ display: 'none' }}
                            />
                            <button
                                data-testid="discard"
                                onClick={() => void discard()}
                                title={t('compose.discard')}
                                style={{
                                    height: 34,
                                    padding: '0 12px',
                                    background: 'transparent',
                                    border: '1px solid var(--border2)',
                                    borderRadius: 'var(--r-btn)',
                                    color: 'var(--text-dim)',
                                    fontSize: 'var(--fs-sm)',
                                }}
                            >
                                {t('compose.discard')}
                            </button>
                            <button
                                onClick={() => fileRef.current?.click()}
                                style={{
                                    height: 34,
                                    padding: '0 12px',
                                    background: 'transparent',
                                    border: '1px solid var(--border2)',
                                    borderRadius: 'var(--r-btn)',
                                    color: 'var(--text-dim)',
                                    fontSize: 'var(--fs-sm)',
                                }}
                            >
                                {t('compose.attach')}
                            </button>
                            <div style={{ flex: 1 }} />
                            <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)' }}>
                                {[
                                    { id: true, label: t('compose.richText') },
                                    { id: false, label: t('compose.plainText') },
                                ].map((o) => (
                                    <button
                                        key={String(o.id)}
                                        onClick={() => setFormat(o.id)}
                                        style={{
                                            height: 24,
                                            padding: '0 10px',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 'var(--fs-xs)',
                                            fontWeight: o.id === rich ? 600 : 400,
                                            background: o.id === rich ? 'var(--surface)' : 'transparent',
                                            /* Aynı ölçüm, aynı sebep: seçili olmayan seçenek
                                               --chip-bg üstünde 4.38:1. Açık yazma penceresi de
                                               bugüne kadar hiç taranmamıştı. */
                                            color: o.id === rich ? 'var(--accent-strong)' : 'var(--text-dim)',
                                        }}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const chrome: React.CSSProperties = {
    width: 26,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text-dim)',
    fontSize: 'var(--fs-md)',
    lineHeight: 1,
};
